import { FC, useEffect, useMemo, useState } from 'react'
import {
  ArrowLeftIcon,
  DocumentArrowDownIcon,
} from '@heroicons/react/24/outline'
import { v4 as uuidv4 } from 'uuid'
import { importShipmentsFromXLSX } from '../../utils/imports'
import {
  FormFile,
  MainPage,
  PageTitle,
  PrimaryButton,
  LinkTextIcon,
  ModalLoading,
  ConfirmModalStatus,
} from '../../components'
import { ImportInterface } from '../../interfaces/Import/importInterfase'
import {
  ShipmentGroup,
  ShipmentPieceRow,
} from '../../interfaces/Import/ShipmentGroupInterface'
import {
  BusinessType,
  BusinessUnitInterface,
  DeliveryEnum,
  PaymentModeId,
} from '../../interfaces'
import { useAppSelector } from '../../store/hooks'
import {
  getShipmentsExists,
  getFavoriteAndHistoricalLocations,
} from '../../services'
import { exportFormatoShipmentXLSX_V2WithCoords } from '../../utils/exports'
import {
  normalizePhone,
  parseFavoriteIdentifier,
  cleanStoreCode,
  extractCoord,
} from '../../utils/shipmentImportUtils'
import { DestinationInterface } from '../../interfaces/Account/DestinationInterface'
import ShipmentBulkUploadReview from './ShipmentBulkUploadReview'

interface NormalizationContext {
  typeShipment: { value: string; name: string }[]
  citiesType: any[]
  citiesByBuType: any[]
  businessUnits: BusinessUnitInterface[]
  identificationTypes: any[]
  pieceCategories: any[]
  user: any
}

const normalizeShipmentByVersion = (
  shipment: ImportInterface,
  version: 'V1' | 'V2',
  context: NormalizationContext
) => {
  const {
    typeShipment,
    citiesType,
    citiesByBuType,
    businessUnits,
    identificationTypes,
    pieceCategories,
    user,
  } = context

  shipment.A = shipment.A?.toString()
  shipment.B = shipment.B?.toString()

  // G.4a fix: trim Excel cell value before matching. User report: cells with
  // trailing whitespace from copy-paste fell through and defaulted to '' → UI
  // rendered as if OFFICE because deliveryType empty. Trim + normalize case.
  const rawDeliveryType = shipment.C?.toString().trim().toUpperCase() ?? ''
  shipment.C =
    typeShipment.find(
      (x) =>
        x.value.toString().toUpperCase() === rawDeliveryType ||
        x.name.toString().toUpperCase() === rawDeliveryType
    )?.value ??
    // Carga masiva-only: "Tienda" es el label visible de OFFICE en la plantilla; el
    // backend no lo conoce, así que lo mapeamos explícitamente. 'OFICINA' se mantiene
    // por compatibilidad con plantillas viejas.
    (rawDeliveryType === 'TIENDA' || rawDeliveryType === 'OFICINA'
      ? DeliveryEnum.OFFICE
      : '')

  shipment.D =
    citiesType.find(
      (x) =>
        x.locationCode.toString().toUpperCase() ===
          shipment.D?.toString().toUpperCase() ||
        x.locationName.toString().toUpperCase() ===
          shipment.D?.toString().toUpperCase()
    )?.locationCode ?? ''

  let rawStoreCode = shipment.E?.toString().trim()
  if (version === 'V2' && rawStoreCode)
    rawStoreCode = cleanStoreCode(rawStoreCode)

  const shouldInferStore =
    version === 'V1' || (version === 'V2' && shipment.C === DeliveryEnum.OFFICE)
  const inferredStoreCode =
    shouldInferStore && (!rawStoreCode || rawStoreCode.length === 0)
      ? citiesByBuType.find(
          (x) =>
            x.locationCode ===
            citiesType.find(
              (c) =>
                c.locationCode.toString().toUpperCase() ===
                  shipment.D?.toString().toUpperCase() ||
                c.locationName.toString().toUpperCase() ===
                  shipment.D?.toString().toUpperCase()
            )?.locationCode
        )?.buCode
      : ''

  const finalStoreCodeToSearch = rawStoreCode || inferredStoreCode || ''
  let foundStore = businessUnits.find(
    (x) =>
      x.buCode.toString().toUpperCase() === finalStoreCodeToSearch.toUpperCase()
  )
  // Fallback: match against the full dropdown value "${buCode} - ${buArea}" in case
  // cleanStoreCode didn't strip correctly (e.g., buCode contains internal hyphens).
  if (!foundStore && shipment.E) {
    const original = String(shipment.E).trim().toUpperCase()
    foundStore = businessUnits.find((x) => {
      const area =
        ((x as any).buArea?.toString().toUpperCase() ||
          x.buCode?.toString().toUpperCase()) ??
        ''
      const dropdownStr = `${x.buCode?.toString().toUpperCase()} - ${area}`
      return dropdownStr === original
    })
  }
  shipment.E = foundStore?.buCode ?? ''

  if (version === 'V2' && foundStore?.location.locationCode && !shipment.D) {
    shipment.D =
      foundStore.location.locationCode ??
      citiesType.find(
        (x) =>
          x.locationCode.toString().toUpperCase() ===
            shipment.D?.toString().toUpperCase() ||
          x.locationName.toString().toUpperCase() ===
            shipment.D?.toString().toUpperCase()
      )?.locationCode ??
      ''
  }

  if (version === 'V2' && shipment.C === DeliveryEnum.OFFICE && foundStore) {
    if (
      foundStore.location.address &&
      foundStore.location.address.trim().length > 0
    ) {
      shipment.N = foundStore.location.address
    }
  }

  shipment.F =
    identificationTypes
      .find(
        (x) =>
          (x.abbreviationName === user?.client?.abbreviationName &&
            (shipment.F === undefined ||
              shipment.F?.toString().trim().length === 0)) ||
          x.taxIdentificationTypeId?.toString() ===
            shipment.F?.toString().toUpperCase() ||
          x.abbreviationName?.slice(0, 1) ===
            shipment.F?.toString().toUpperCase() ||
          x.abbreviationName.toUpperCase() ===
            shipment.F?.toString().toUpperCase()
      )
      ?.taxIdentificationTypeId?.toString() ?? ''

  shipment.I =
    identificationTypes
      .find(
        (x) =>
          x.taxIdentificationTypeId.toString() ===
            shipment.I?.toString().toUpperCase() ||
          x.abbreviationName.slice(0, 1) ===
            shipment.I?.toString().toUpperCase() ||
          x.abbreviationName.toUpperCase() ===
            shipment.I?.toString().toUpperCase()
      )
      ?.taxIdentificationTypeId.toString() ?? ''

  shipment.G =
    shipment.G !== undefined &&
    shipment.G !== null &&
    shipment.G?.toString().trim().length > 0
      ? shipment.G?.toString().trim()
      : (user?.client?.identificationNumber?.toString().trim() ?? '')

  // Nombre Remitente vacío → usar el nombre del CLIENTE autorizado seleccionado
  // (listAuthorizingAccountClient[0].accountFullName), NO el accountFullName del sitio
  // (que es el accountSiteName). Mismo criterio que Inventory.tsx:281-283.
  shipment.H =
    shipment.H !== undefined &&
    shipment.H !== null &&
    shipment.H?.toString().trim().length > 0
      ? shipment.H?.toString().trim()
      : (user?.client?.listAuthorizingAccountClient?.[0]?.accountFullName
          ?.toString()
          .trim() ?? '')

  shipment.J = shipment.J?.toString().trim()
  shipment.K = shipment.K?.toString().trim()
  shipment.L = shipment.L?.toString().trim()
  shipment.M = shipment.M?.toString().trim()
  shipment.N = shipment.N?.toString().trim()
  shipment.O = shipment.O?.toString().trim()
  shipment.P = shipment.P?.toString().trim()
  shipment.Q = shipment.Q?.toString().trim()
  shipment.R = shipment.R?.toString()?.replace(',', '.').trim()
  shipment.S = shipment.S?.toString().trim()
  shipment.T = shipment.T?.toString().trim()
  shipment.U = shipment.U?.toString().trim()
  shipment.V = shipment.V?.toString().trim()

  if (version === 'V2') {
    const pName = shipment.W?.toString().trim()
    const pCode = shipment.X?.toString().trim()
    let catId = ''
    if (pCode) catId = pieceCategories.find((c) => c.code === pCode)?.id
    if (!catId && pName)
      catId = pieceCategories.find((c) => c.name === pName)?.id
    shipment.W = catId || '49'
  } else {
    shipment.W =
      pieceCategories.find((x) => x.id === shipment.W?.toString().trim())?.id ??
      ''
  }
}

const ShipmentBulkUpload: FC = () => {
  const user = useAppSelector((state) => state.user)
  const businessUnits = useAppSelector((state) => state.inmutable.businessUnits)
  const identificationTypes = useAppSelector(
    (state) => state.inmutable.taxIdentificationTypes
  )
  const citiesType = useAppSelector((state) => state.inmutable.cities)
  const citiesByBuType = useAppSelector((state) => state.inmutable.citiesByBu)
  const pieceCategories = useAppSelector(
    (state) => state.inmutable.PieceCategories
  )

  // Carga masiva-only: OFFICE se muestra como "Tienda" (consistente con la plantilla
  // Excel). El valor interno sigue siendo DeliveryEnum.OFFICE.
  const typeShipment = [
    { value: DeliveryEnum.OFFICE, name: 'Tienda' },
    { value: DeliveryEnum.DELIVERY, name: 'Domicilio' },
  ]

  const [error, setError] = useState('')
  const [file, setFile] = useState<File>()
  const [groups, setGroups] = useState<ShipmentGroup[]>([])
  const [nextSave, setNextSave] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loadingStatus, setLoadingStatus] = useState(
    ConfirmModalStatus.PROCESSING
  )
  const [loadingTitle, setLoadingTitle] = useState('Cargando...')
  const [initialValidationError, setInitialValidationError] = useState<
    string | null
  >(null)
  const [initialValidationDone, setInitialValidationDone] = useState(false)
  const [draftInfo, setDraftInfo] = useState<{
    fileName: string
    savedAt: number
    groups: ShipmentGroup[]
  } | null>(null)
  const [favoriteDestinations, setFavoriteDestinations] = useState<
    DestinationInterface[]
  >([])
  const [favoritesLoaded, setFavoritesLoaded] = useState(false)
  const [v2TemplateError, setV2TemplateError] = useState<{
    missing: string[]
  } | null>(null)

  // Validación inicial (businessUnit + cuenta para facturar)
  useEffect(() => {
    if (!user || initialValidationDone) return
    const missingData: string[] = []
    if (!user.client?.businessUnit) missingData.push('tienda origen asignada')
    const paymentModeid =
      user.paymentMethod?.[0]?.paymentModeID ?? PaymentModeId.CREDIT
    const accountBillTo = user.paymentMethod?.filter(
      (x) => x.paymentModeID?.toString() === paymentModeid?.toString()
    )?.[0]?.accountBillTo
    if (
      (accountBillTo === '00000000-0000-0000-0000-000000000000' ||
        accountBillTo === undefined) &&
      paymentModeid.toString() === PaymentModeId.CREDIT.toString()
    ) {
      missingData.push('cuenta para facturar asignada')
    }
    if (missingData.length > 0) {
      const msg =
        missingData.length === 2
          ? 'No tiene cuenta para facturar asignada y no tiene tienda origen asignada. Por favor comuníquese con Soporte Técnico.'
          : `Comuníquese con el departamento de Soporte Técnico. No tiene ${missingData[0]}.`
      setLoadingStatus(ConfirmModalStatus.ERROR)
      setLoadingTitle(msg)
      setInitialValidationError(msg)
      setLoading(true)
    }
    setInitialValidationDone(true)
  }, [user, initialValidationDone])

  // Load favoriteDestinations once (needed for Excel template download + Review screen)
  useEffect(() => {
    const accountId = user?.client?.id
    if (!accountId || favoritesLoaded) return
    getFavoriteAndHistoricalLocations(
      accountId,
      identificationTypes,
      businessUnits,
      ''
    )
      .then((res) => {
        if (!res.didError && res.model) {
          setFavoriteDestinations(res.model.favoriteDestinations || [])
        }
      })
      .catch(() => {
        /* silent — template just won't have dropdown */
      })
      .finally(() => setFavoritesLoaded(true))
  }, [user?.client?.id, identificationTypes, businessUnits, favoritesLoaded])

  // Load draft from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem('shipment-import-v2-draft')
      if (saved) {
        const draft = JSON.parse(saved)
        if (
          draft.version === 1 &&
          Array.isArray(draft.groups) &&
          draft.groups.length > 0
        ) {
          setDraftInfo(draft)
        }
      }
    } catch (_) {}
  }, [])

  // Auto-save draft (debounced 300ms)
  useEffect(() => {
    if (!nextSave || groups.length === 0) return
    const timer = setTimeout(() => {
      try {
        localStorage.setItem(
          'shipment-import-v2-draft',
          JSON.stringify({
            version: 1,
            savedAt: Date.now(),
            fileName: file?.name || '',
            groups,
          })
        )
      } catch (_) {}
    }, 300)
    return () => clearTimeout(timer)
  }, [groups, nextSave, file])

  const handleRestoreDraft = () => {
    if (!draftInfo) return
    setGroups(draftInfo.groups)
    setNextSave(true)
    setDraftInfo(null)
  }

  const handleDiscardDraft = () => {
    localStorage.removeItem('shipment-import-v2-draft')
    setDraftInfo(null)
  }

  const isCOD =
    user.paymentMethod?.some((m: any) => m.paymentModeID?.toString() === '2') ??
    false

  const businessUnitOptions = useMemo(() => {
    const business = businessUnits.filter(
      (bu) =>
        bu.buCode !== 'KK' &&
        bu.buCode !== 'NN' &&
        !bu.isLocked &&
        (bu.buTypeID === BusinessType.EXTERNAL ||
          bu.buTypeID === BusinessType.INTERNAL)
    )
    const uniqueIds = new Set<any>()
    const result: BusinessUnitInterface[] = []
    business.forEach((bu) => {
      if (!uniqueIds.has(bu.buid)) {
        uniqueIds.add(bu.buid)
        result.push(bu)
      }
    })
    return result
  }, [businessUnits])

  const splitString = (str: string) => {
    const arr = str.split(',')
    const result: string[] = []
    let temp = ''
    let count = 0
    for (const item of arr) {
      temp += item
      count += item.length
      if (count >= 200) {
        result.push(temp)
        temp = ''
        count = 0
      } else {
        temp += ','
        count++
      }
    }
    if (temp) result.push(temp.replace(/,$/, ''))
    return result
  }

  const handleFileChange = (f?: File) => {
    if (initialValidationError) {
      setError(initialValidationError)
      return
    }
    if (!f) {
      setFile(undefined)
      return
    }
    const fileType = f.type
    if (
      fileType === 'text/csv' ||
      fileType === 'application/vnd.ms-excel' ||
      fileType ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ) {
      setFile(f)
      setError('')
    } else {
      setError('El archivo no es csv, xls o xlsx')
    }
  }

  const handleFileSubmit = async () => {
    if (!file) {
      setError('Por favor seleccione un archivo')
      return
    }
    setError('')
    setLoading(true)
    setLoadingStatus(ConfirmModalStatus.PROCESSING)
    // H.1: show the actual filename like V1 does, so operator knows which file
    // is being processed (defensive against confused click-twice flows).
    setLoadingTitle(`Procesando archivo: ${file.name}`)

    try {
      let result = await importShipmentsFromXLSX(file)

      // Build headerMap: header name (lowercase) → column letter
      const rawHeaderRow = result[0] as Record<string, any>
      result.shift()

      const headerMap: Record<string, string> = {}
      Object.entries(rawHeaderRow).forEach(([letter, value]) => {
        if (value != null) {
          headerMap[String(value).trim().toLowerCase()] = letter
        }
      })

      // Read cell value by header name
      const col = (row: Record<string, any>, name: string): string => {
        const letter = headerMap[name.toLowerCase()]
        return letter ? String(row[letter] ?? '') : ''
      }

      // H.4: read raw cell value (pre-stringification) so extractCoord can handle
      // SheetJS Number values + Excel rich cell objects. Plain col() stringifies
      // to "[object Object]" which kills parseFloat. Helper colocated here so it
      // closes over headerMap; extractCoord itself lives in shipmentImportUtils.ts.
      const colRaw = (row: Record<string, any>, name: string): any => {
        const letter = headerMap[name.toLowerCase()]
        return letter ? row[letter] : undefined
      }

      // H.4: hoist debug flag check out of the per-row loop (was N reads for N rows).
      const debugImport =
        typeof window !== 'undefined' &&
        window.localStorage?.getItem('debug-import-v2') === '1'

      // V4: 27 required headers (Fecha eliminada; renombres + swap Ciudad↔Tienda).
      // DEBEN coincidir exactamente con el array `headers` de
      // exportFormatoShipmentXLSX_V2WithCoords (exports.tsx) — match por texto, no letra.
      const REQUIRED_HEADERS = [
        'Número de guía aliada',
        'Tipo de documento Remitente (Opcional)',
        'Nº de Documento Remitente (Opcional)',
        'Nombre Remitente (Opcional)',
        '¿Es destinatario favorito? (Sí/No)',
        'Identificador destinatario favorito',
        'Tipo de documento destinatario',
        'Nº de Documento destinatario',
        'Nombre destinatario',
        'Telefono destinatario',
        'Correo destinatario',
        'Tipo de entrega',
        'Ciudad/Localidad destino (si es domicilio)',
        'Código de tienda destino (si es entrega en tienda)',
        'Dirección Destino',
        'Latitud destino (si es domicilio)',
        'Codigo Postal (Opcional)',
        'Longitud destino (si es domicilio)',
        'Categoria del contenido',
        'Código de categoría',
        'Cantidad de Piezas',
        'Peso en Kg por pieza',
        'Dimension Alto por pieza',
        'Dimension Ancho por pieza',
        'Dimension Largo por pieza',
        'Valor declarado por pieza',
        'Observaciones',
      ]
      const missing = REQUIRED_HEADERS.filter(
        (h) => !headerMap[h.toLowerCase()]
      )
      // Check if this is a V2-era template (missing only the two new favorite headers)
      const onlyFavMissing =
        missing.length === 2 &&
        missing.includes('¿Es destinatario favorito? (Sí/No)') &&
        missing.includes('Identificador destinatario favorito')
      if (missing.length > 0) {
        if (onlyFavMissing) {
          // Friendly V2 → V3 migration modal
          setLoading(false)
          setV2TemplateError({ missing })
          return
        }
        setLoadingStatus(ConfirmModalStatus.ERROR)
        setLoadingTitle(
          `Formato incorrecto — headers faltantes: ${missing.slice(0, 3).join(', ')}${missing.length > 3 ? '...' : ''}. Por favor descarga el nuevo template con el botón Descargar.`
        )
        return
      }

      // Filter empty rows (solo basado en el número de guía; Fecha ya no existe)
      result = result.filter((row) => {
        return col(row, 'Número de guía aliada').trim().length > 0
      })

      if (result.length === 0) {
        setLoadingStatus(ConfirmModalStatus.ERROR)
        setLoadingTitle(
          'El archivo no contiene guías válidas. Verifique que la columna "Número de guía aliada" tenga valores.'
        )
        return
      }

      // Ciudad llega como "Localidad - Código"; extraemos el código (último segmento
      // tras " - "). Si no trae sufijo, se pasa tal cual (normalize matchea por nombre).
      const cityCodeFromCell = (rawCity: string): string => {
        const s = (rawCity || '').trim()
        if (!s) return ''
        const i = s.lastIndexOf(' - ')
        return i >= 0 ? s.slice(i + 3).trim() : s
      }

      // Build semantic objects for normalizeShipmentByVersion.
      // Las LETRAS del ImportInterface son el contrato interno de normalize (no las letras
      // de la plantilla); solo cambian los NOMBRES de header que se leen con col().
      const toSemantic = (raw: Record<string, any>): ImportInterface =>
        ({
          A: '',
          B: col(raw, 'Número de guía aliada'),
          C: col(raw, 'Tipo de entrega'),
          D: cityCodeFromCell(col(raw, 'Ciudad/Localidad destino (si es domicilio)')),
          E: col(raw, 'Código de tienda destino (si es entrega en tienda)'),
          F: col(raw, 'Tipo de documento Remitente (Opcional)'),
          G: col(raw, 'Nº de Documento Remitente (Opcional)'),
          H: col(raw, 'Nombre Remitente (Opcional)'),
          I: col(raw, 'Tipo de documento destinatario'),
          J: col(raw, 'Nº de Documento destinatario'),
          K: col(raw, 'Nombre destinatario'),
          L: col(raw, 'Telefono destinatario'),
          M: col(raw, 'Correo destinatario'),
          N: col(raw, 'Dirección Destino'),
          O: col(raw, 'Codigo Postal (Opcional)'),
          P: col(raw, 'Observaciones'),
        }) as ImportInterface

      const semanticRows = result.map((raw) => toSemantic(raw))
      semanticRows.forEach((shipment) => {
        normalizeShipmentByVersion(shipment, 'V2', {
          typeShipment,
          citiesType,
          citiesByBuType,
          businessUnits,
          identificationTypes,
          pieceCategories,
          user,
        })
      })

      // Check duplicates in DB
      setLoadingTitle('Verificando guías existentes...')
      const trackingValues = result.map((r) =>
        col(r, 'Número de guía aliada').trim().toUpperCase()
      )
      const concatenatedB = trackingValues.join(',')
      const bChunks = splitString(concatenatedB)
      const accountCode = user?.client?.accountCode ?? ''
      const accountId = user?.client?.id ?? ''
      const existsParts: string[] = []
      for (const chunk of bChunks) {
        const part = await getShipmentsExists(
          chunk,
          accountCode,
          accountCode === '' ? accountId : undefined
        )
        // model:[] es array vacío (truthy pero sin datos) — solo agregar si es string no vacío
        if (typeof part === 'string' && part.trim().length > 0) {
          existsParts.push(part)
        } else if (Array.isArray(part) && part.length > 0) {
          existsParts.push(part.map((s: any) => String(s)).join(','))
        }
      }
      const existsStr = existsParts.join(',')
      const existingSet = new Set<string>(
        existsStr
          .split(',')
          .map((s) => s.trim().toUpperCase())
          .filter(Boolean)
      )
      // COD: pre-resolve shipper doc type from user account
      const userShipperDocTypeId =
        identificationTypes
          .find((t) => t.abbreviationName === user.client?.abbreviationName)
          ?.taxIdentificationTypeId?.toString() ?? ''

      // Category derivation: code or name → category id (string/number safe)
      const deriveCategoryId = (name: string, code: string): string => {
        const trimmedCode = code?.trim()
        const trimmedName = name?.trim()
        const byCode = pieceCategories.find(
          (c) =>
            String(c.code || '').trim() === trimmedCode ||
            String(c.id || '').trim() === trimmedCode
        )
        if (byCode) return String(byCode.id)
        const byName = pieceCategories.find(
          (c) =>
            String(c.name || '')
              .trim()
              .toLowerCase() === trimmedName?.toLowerCase()
        )
        if (byName) return String(byName.id)
        // 5A: si el usuario escribió categoría/código pero no resolvió contra la lista,
        // devolver '' para que validatePiece marque la fila como error (en vez de
        // enmascararlo con la categoría por defecto). Solo cae al default si vino vacío.
        if (trimmedCode || trimmedName) return ''
        return '49'
      }

      // Piece deduplication key: productCode + weight + height + width + length
      const pieceKey = (raw: Record<string, any>): string =>
        [
          col(raw, 'Código de categoría'),
          col(raw, 'Peso en Kg por pieza'),
          col(raw, 'Dimension Alto por pieza'),
          col(raw, 'Dimension Ancho por pieza'),
          col(raw, 'Dimension Largo por pieza'),
        ]
          .map((v) => String(v || '').trim())
          .join('|')

      // Group by tracking number — identical pieces are merged (sum quantity)
      const groupMap = new Map<string, ShipmentGroup>()
      const pieceMaps = new Map<string, Map<string, ShipmentPieceRow>>()

      for (let idx = 0; idx < result.length; idx++) {
        const raw = result[idx]
        const sem = semanticRows[idx]

        const tracking = col(raw, 'Número de guía aliada')
          .trim()
          .toUpperCase()
        if (!tracking) continue

        if (!groupMap.has(tracking)) {
          // H.4: read raw cell for coord columns so the defensive extractor can handle
          // SheetJS number values, comma-decimal strings, and rich cell objects uniformly.
          const rawLat = colRaw(raw, 'Latitud destino (si es domicilio)')
          const rawLng = colRaw(raw, 'Longitud destino (si es domicilio)')

          // V3: check if Excel row marks this as favorite + has valid identifier
          const esFavoritoRaw = col(raw, '¿Es destinatario favorito? (Sí/No)')
            .trim()
            .toLowerCase()
          const esFavorito =
            esFavoritoRaw === 'sí' ||
            esFavoritoRaw === 'si' ||
            esFavoritoRaw === 's' ||
            esFavoritoRaw === 'yes' ||
            esFavoritoRaw === 'y'
          // H.2: explicit "No" means operator opted out — don't nag with RIF auto-suggest
          // later. Blank/empty is different (operator didn't decide).
          const optedOutOfFavorite =
            esFavoritoRaw === 'no' || esFavoritoRaw === 'n'
          const favIdentifierRaw = col(
            raw,
            'Identificador destinatario favorito'
          ).trim()
          // V3+E: column G now holds account.displayName (from the hidden Favoritos sheet).
          // Match by displayName first; fall back to legacy RIF parser for backward compat.
          let matchedFav: (typeof favoriteDestinations)[number] | undefined
          if (esFavorito && favIdentifierRaw) {
            matchedFav = favoriteDestinations.find(
              (f) => (f.account?.displayName || '').trim() === favIdentifierRaw
            )
            if (!matchedFav) {
              const favRif = parseFavoriteIdentifier(favIdentifierRaw)
              if (favRif) {
                matchedFav = favoriteDestinations.find(
                  (f) =>
                    (f.account?.identificationNumber || '').trim() === favRif
                )
              }
            }
          }

          // Build base group from Excel fields (will be overridden below if fav match)
          // V3+: lat/lng only apply to home delivery. Force null when OFFICE.
          const isOfficeDelivery = sem.C === DeliveryEnum.OFFICE
          // H.4: extractCoord handles Number, string-with-comma, rich cell object.
          // Replaces the old safeParseCoord which only handled strings.
          const parsedLat = extractCoord(rawLat)
          const parsedLng = extractCoord(rawLng)
          if (debugImport) {
            // eslint-disable-next-line no-console
            console.log('[import-v2] coord parse', {
              tracking,
              rawLat,
              rawLng,
              rawLatType: typeof rawLat,
              rawLngType: typeof rawLng,
              parsedLat,
              parsedLng,
              deliveryType: sem.C,
              isOfficeDelivery,
            })
          }
          const baseGroup: ShipmentGroup = {
            // crypto.randomUUID requires secure context (HTTPS). uuid v4 works everywhere.
            id: uuidv4(),
            trackingNumber: tracking,
            deliveryType: sem.C,
            destinationCity: sem.D,
            storeCode: sem.E,
            shipperDocType: isCOD ? userShipperDocTypeId : sem.F,
            shipperDocNumber: isCOD
              ? (user.client?.identificationNumber ?? '')
              : sem.G,
            shipperName: isCOD
              ? (user.client?.listAuthorizingAccountClient?.[0]?.accountFullName ??
                '')
              : sem.H,
            consigneeDocType: sem.I,
            consigneeDocNumber: sem.J,
            consigneeName: sem.K,
            consigneePhone: normalizePhone(sem.L),
            consigneeEmail: sem.M,
            destinationAddress: sem.N,
            postalCode: sem.O,
            notes: sem.P,
            latitude: isOfficeDelivery ? null : parsedLat,
            longitude: isOfficeDelivery ? null : parsedLng,
            // G.5: punto de referencia (lee directo del raw para mantener toSemantic estable).
            // Backward compat: si el template viejo no tiene esta columna, col() retorna ''.
            addressReference: isOfficeDelivery
              ? ''
              : col(raw, 'Punto de referencia (Opcional)'),
            pieces: [],
            isShipperLocked: isCOD,
            isDuplicateInDB: existingSet.has(tracking),
            // H.2: persist explicit F=No so RIF auto-suggest banner doesn't fire later.
            optedOutOfFavorite: optedOutOfFavorite || undefined,
          }

          // V3: if Excel marked favorite + matched, override consignee fields from the favorite
          if (matchedFav) {
            const acc = matchedFav.account
            const loc = matchedFav.location
            const bu = matchedFav.businessUnit
            baseGroup.consigneeDocType =
              acc?.taxIdentificationTypeID == null
                ? ''
                : String(acc.taxIdentificationTypeID)
            baseGroup.consigneeDocNumber = acc?.identificationNumber || ''
            baseGroup.consigneeName = acc?.accountFullName || ''
            baseGroup.consigneePhone = normalizePhone(
              acc?.listAccountPhone?.[0]?.phoneNumber || ''
            )
            baseGroup.consigneeEmail = acc?.listAccountEmail?.[0]?.email || ''
            baseGroup.destinationAddress =
              loc?.address || baseGroup.destinationAddress
            baseGroup.postalCode = loc?.postalCode || baseGroup.postalCode
            baseGroup.storeCode = bu?.buCode || baseGroup.storeCode
            baseGroup.destinationCity =
              bu?.location?.locationCode || baseGroup.destinationCity

            // G.4-bis (CEO Review): detect silent override of Excel-supplied deliveryType.
            // If user wrote 'Domicilio' in M but the favorite is office (or vice versa),
            // the favorite was winning silently — violates "no silent failures" rule.
            // Now we surface the override as a visible warning attached to the group.
            const favDeliveryType = matchedFav.isHomeDelivery
              ? DeliveryEnum.DELIVERY
              : DeliveryEnum.OFFICE
            const excelDeliveryType = baseGroup.deliveryType
            if (excelDeliveryType && excelDeliveryType !== favDeliveryType) {
              const excelLabel =
                excelDeliveryType === DeliveryEnum.DELIVERY
                  ? 'Domicilio'
                  : 'Tienda'
              const favLabel = matchedFav.isHomeDelivery ? 'Domicilio' : 'Tienda'
              baseGroup.importWarning =
                `Tipo de entrega del Excel (${excelLabel}) reemplazado por el del favorito (${favLabel}). ` +
                `Si quieres entrega ${excelLabel}, cambia "¿Es destinatario favorito?" a No o elige otro favorito.`
            }
            baseGroup.deliveryType = favDeliveryType
            // V3+: lat/lng only apply to home delivery favorites. Office favorites stay coordinate-free.
            if (matchedFav.isHomeDelivery) {
              baseGroup.latitude = loc?.coordinates?.lat ?? baseGroup.latitude
              baseGroup.longitude = loc?.coordinates?.lng ?? baseGroup.longitude
            } else {
              baseGroup.latitude = null
              baseGroup.longitude = null
            }
            baseGroup.favoriteDestinationId = matchedFav.id || acc?.id || ''
          }

          groupMap.set(tracking, baseGroup)
          pieceMaps.set(tracking, new Map<string, ShipmentPieceRow>())
        }

        const pMap = pieceMaps.get(tracking)!
        const pKey = pieceKey(raw)
        const qty = parseInt(col(raw, 'Cantidad de Piezas')) || 1

        if (pMap.has(pKey)) {
          const ep = pMap.get(pKey)!
          ep.quantity += qty
          ep.mergedRowCount = (ep.mergedRowCount || 1) + 1
        } else {
          pMap.set(pKey, {
            quantity: qty,
            weight:
              parseFloat(col(raw, 'Peso en Kg por pieza').replace(',', '.')) ||
              0,
            height:
              parseFloat(
                col(raw, 'Dimension Alto por pieza').replace(',', '.')
              ) || 0,
            width:
              parseFloat(
                col(raw, 'Dimension Ancho por pieza').replace(',', '.')
              ) || 0,
            length:
              parseFloat(
                col(raw, 'Dimension Largo por pieza').replace(',', '.')
              ) || 0,
            declaredValue:
              parseFloat(
                col(raw, 'Valor declarado por pieza').replace(',', '.')
              ) || 0,
            productName: col(raw, 'Categoria del contenido'),
            productCode: col(raw, 'Código de categoría'),
            categoryId: deriveCategoryId(
              col(raw, 'Categoria del contenido'),
              col(raw, 'Código de categoría')
            ),
            mergedRowCount: 1,
          })
        }
      }

      // Attach pieces to groups
      const grouped = Array.from(groupMap.values()).map((g) => ({
        ...g,
        pieces: Array.from(
          pieceMaps.get(g.trackingNumber) || new Map<string, ShipmentPieceRow>()
        ).map(([, p]) => p),
      }))

      setGroups(grouped)
      setNextSave(true)
      setLoading(false)
    } catch (err: any) {
      console.error(err)
      setLoadingStatus(ConfirmModalStatus.ERROR)
      setLoadingTitle(err?.message || 'Error procesando el archivo')
    }
  }

  useEffect(() => {
    if (!nextSave) {
      setError('')
      setFile(undefined)
      setGroups([])
    }
  }, [nextSave])

  return (
    <MainPage>
      <PageTitle title="Cargar guías" />

      {draftInfo && !nextSave && (
        <div className="w-full max-w-xl mx-auto mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <span className="text-sm text-amber-800">
            Tienes un borrador guardado (
            {draftInfo.fileName || 'archivo desconocido'},{' '}
            {new Date(draftInfo.savedAt).toLocaleDateString('es-VE')}). ¿Deseas
            restaurarlo?
          </span>
          <div className="flex gap-2 flex-shrink-0">
            <PrimaryButton
              onClick={handleRestoreDraft}
              className="h-8 text-xs px-3"
            >
              Restaurar
            </PrimaryButton>
            <button
              type="button"
              onClick={handleDiscardDraft}
              className="h-8 text-xs text-gray-500 hover:text-gray-700 px-2"
            >
              Descartar
            </button>
          </div>
        </div>
      )}

      {!nextSave && (
        <div className="flex flex-1 flex-col items-center justify-center mt-6 gap-4 max-w-xl mx-auto w-full">
          <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex flex-col items-center gap-3 p-4 rounded-lg border border-gray-200 bg-white text-center">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-50 text-blue-600 font-bold text-sm">
                1
              </span>
              <p className="font-semibold text-sm text-gray-800">
                Descarga el formato
              </p>
              <PrimaryButton
                onClick={() => {
                  const hasCredit = !!user.paymentMethod?.find(
                    (x: any) => x.paymentModeID.toString() === '3'
                  )
                  exportFormatoShipmentXLSX_V2WithCoords(
                    identificationTypes,
                    typeShipment,
                    businessUnitOptions,
                    citiesType,
                    pieceCategories,
                    !hasCredit,
                    favoriteDestinations
                  )
                }}
                disabled={!favoritesLoaded}
                className="w-full h-11 text-sm"
              >
                <DocumentArrowDownIcon className="w-4 h-4 mr-1" />
                {favoritesLoaded ? 'Descargar' : 'Cargando favoritos…'}
              </PrimaryButton>
            </div>

            <div className="flex flex-col items-center gap-3 p-4 rounded-lg border border-gray-200 bg-white text-center">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-500 font-bold text-sm">
                2
              </span>
              <p className="font-semibold text-sm text-gray-800">Complétalo</p>
              <p className="text-xs text-gray-500">
                Llena los datos de tus guías en el archivo
              </p>
            </div>
          </div>

          <div className="w-full rounded-lg border border-gray-200 bg-white p-4">
            <div className="flex flex-col items-center gap-3 mb-3 text-center">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-500 font-bold text-sm">
                3
              </span>
              <p className="font-semibold text-sm text-gray-800">Súbelo aquí</p>
            </div>
            <FormFile
              selected={file}
              error={error}
              label="Selecciona tu archivo"
              description="Archivos admitidos: CSV | XLS | XLSX"
              id="shipment-import-v2-file"
              name="shipment-import-v2-file"
              onSelectFile={handleFileChange}
            />
            <div className="mt-3">
              <PrimaryButton
                onClick={handleFileSubmit}
                className="w-full h-11"
                disabled={!file || !favoritesLoaded}
              >
                {favoritesLoaded ? 'Cargar archivo' : 'Cargando favoritos…'}
              </PrimaryButton>
              {!favoritesLoaded && file && (
                <p className="mt-2 text-xs text-amber-700">
                  Esperando favoritos antes de importar — evita que las filas
                  con "Es destinatario favorito = Sí" se importen sin datos.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {nextSave && (
        <>
          <div className="flex flex-1 justify-between gap-12 md:gap-12 flex-col-reverse md:flex-row mt-2">
            <LinkTextIcon
              className="text-sm font-medium cursor-pointer"
              onClick={() => setNextSave(false)}
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1" aria-hidden="true" />
              <label>Regresar</label>
            </LinkTextIcon>
          </div>
          <ShipmentBulkUploadReview
            groups={groups}
            setGroups={setGroups}
            setNext={setNextSave}
            isCOD={isCOD}
            businessUnits={businessUnits}
            identificationTypes={identificationTypes}
            citiesType={citiesType}
            citiesByBu={citiesByBuType}
            pieceCategories={pieceCategories}
            favoriteDestinations={favoriteDestinations}
            file={file}
          />
        </>
      )}

      <ModalLoading
        open={loading}
        title={loadingTitle}
        status={loadingStatus}
        setOpen={setLoading}
        onPrint={() => {}}
      />

      {v2TemplateError && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="v2-template-error-title"
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-4 py-3 border-b border-gray-200">
              <h2
                id="v2-template-error-title"
                className="text-red-600 text-base font-semibold"
              >
                Template antiguo detectado
              </h2>
            </div>
            <div className="p-4 text-sm text-gray-700 space-y-2">
              <p>
                Tu archivo usa una versión antigua del template que no incluye
                las columnas de <strong>destinatario favorito</strong>.
              </p>
              <p className="text-xs text-gray-500">
                Faltan: {v2TemplateError.missing.join(', ')}
              </p>
              <p>Descarga el nuevo template y vuelve a cargar tus datos.</p>
            </div>
            <div className="flex justify-end gap-3 px-4 py-3 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setV2TemplateError(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 rounded-md border border-gray-200 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <PrimaryButton
                onClick={() => {
                  const hasCredit = !!user.paymentMethod?.find(
                    (x: any) => x.paymentModeID.toString() === '3'
                  )
                  exportFormatoShipmentXLSX_V2WithCoords(
                    identificationTypes,
                    typeShipment,
                    businessUnitOptions,
                    citiesType,
                    pieceCategories,
                    !hasCredit,
                    favoriteDestinations
                  )
                  setV2TemplateError(null)
                }}
                disabled={!favoritesLoaded}
                className="text-sm"
              >
                Descargar nuevo template
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </MainPage>
  )
}

export default ShipmentBulkUpload
